
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import PictureComp from '@/components/picture/PictureComp.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import SportWatchNowButton from '@/components/sports/all/SportWatchNowButton.vue';
import CompetitorLogo from '@/components/sports/common/CompetitorLogo.vue';
import LiveChip from '@/components/sports/common/LiveChip.vue';
import NoOfferNotifier from '@/components/sports/common/event/NoOfferNotifier.vue';

import { ObjectType, SingleStepSportEvent, SportOffer } from '@/@types/graphql-types';
import { SportLocale, ToggleMySportsNotificationPayload } from '@/enums/sports';
import { Locale, LocaleResponse } from '@/helpers/locale-helper';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { BuyboxParam, trackClickOutSports } from '@/helpers/offer-helper';
import { SingleStepTeamContext, contextSportIs } from '@/helpers/sports/config';
import { formatDate, formatTime } from '@/helpers/time-zone-helper';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'EventEncounterLarge',
	components: {
		CompetitorLogo,
		LiveChip,
		NoOfferNotifier,
		ProviderIcon,
		SportWatchNowButton,
		PictureComp,
	},
})
export default class EventEncounterLarge extends Vue {
	@Prop({ required: true }) listIndex: number;
	@Prop({ default: '' }) domainSessionId: string;
	@Prop({ default: false }) isLive: boolean;
	@Prop({ required: true }) event: SingleStepSportEvent;
	@Prop({ required: true }) context: SingleStepTeamContext;

	@Language.Getter language: string;
	@Language.Getter country: string;
	@Language.State webLocale: SportLocale;
	@Language.State locales: Locale[];
	@Language.Getter localeObject: LocaleResponse;

	@User.State timeZone: string | undefined;

	get competitors() {
		return this.context.getCompetitors(this.event, this.webLocale);
	}

	get isDoublesEvent() {
		return this.competitors.start.length === 2 && this.competitors.end.length === 2;
	}

	get eventTitle() {
		const { key, params } = this.context.getEventTitle(this.event, this.webLocale);
		return this.$t(key, params);
	}

	get stageHeading() {
		return this.context.getStageHeading(this.event.content.competitionStage, this.$t.bind(this));
	}

	get eventDate() {
		return formatDate(
			new Date(this.event.content.startAt),
			this.locales,
			this.webLocale,
			this.timeZone,
			this.language
		);
	}

	get eventTime() {
		return formatTime(
			new Date(this.event.content.startAt),
			this.locales,
			this.webLocale,
			this.timeZone,
			this.language
		);
	}

	get watchNowOffer() {
		return this.event.watchNowOffer as SportOffer | undefined;
	}

	get backdropImageUrl() {
		const sport = contextSportIs.esports(this.context) ? 'ESPORTS' : this.context.sportType;
		return `/${ASSETS_DIR}/img/sports/jw-sports-backdrop-${sport}-scaled.jpeg`;
	}

	getOfferClickoutUrl(offer: SportOffer) {
		return new ClickoutUrl(offer)
			.set('uct_country', this.country)
			.set('uct_buybox', BuyboxParam.Sport)
			.set('sid', this.domainSessionId)
			.set('uct_sport', this.event?.sport)
			.toString();
	}

	// TRACKING
	get sportContext() {
		return SnowplowSportContext.fromSportEventV2(this.event.sport, ObjectType.SportEvent, this.event);
	}

	get notifyMePayload(): ToggleMySportsNotificationPayload {
		return {
			eventId: this.event.id,
			isNotificationActive: !!this.event.mysportlistEntry?.createdAt,
		};
	}

	onCompetitionClick() {
		// tracking
		const sportContext = new SnowplowSportContext(this.event.sport, ObjectType.SportCompetition);
		TrackingHelper.trackEvent('userinteraction', { action: 'competition_clicked', property: 'card-double' }, [
			sportContext,
		]);
	}

	onCardClick() {
		this.trackCardClick();
		this.$router.push({ path: this.event.content.fullPath as string | undefined });
	}

	onClickOut() {
		if (this.watchNowOffer == null) return;

		trackClickOutSports(this.watchNowOffer, 0, this.listIndex, this.watchNowOffer.sportObjectType, 'card-double', [
			this.sportContext,
		]);
	}

	trackCardClick() {
		TrackingHelper.trackEvent('userinteraction', { action: 'event_clicked', property: 'card-double' }, [
			this.sportContext,
		]);
	}

	onNoOfferNotifierClick() {
		TrackingHelper.trackEvent('userinteraction', { action: 'notify_me_clicked', property: 'card-double' }, [
			this.sportContext,
		]);
	}
}
